import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useMemo } from 'react'
import { useInstallationInvitation } from 'src/Libs/installation-rights-management/hooks'
import { useFetchEebusInfoFromEmc } from '../../advanced-settings/charging-stations'
import { fromHeatpumpDtoToModel } from '../../advanced-settings/heatpump'
import { useFetchSmartMeterGateway } from '../../advanced-settings/overview/hooks/use-fetch-smart-meter-getaway'
import { fromMeterDtoToModel } from '../../advanced-settings/powermeter'
import { countryCodes } from '../../constants/countries'
import { useGetBadges } from '../../end-user-settings'
import { getStorageType, parseEmcDeviceLink, parseStorageDeviceLink } from '../../parsers/device-parsers/device-parsers'
import { getCharginStationExpandedState, parseChargingStations } from '../../parsers/device-parsers/evse'
import { parseHtc320hIo, parseMyPvIo, parseStorageIo } from '../../parsers/device-parsers/io'
import { allowAccess } from '../../types/access-right'
import { useLocationEmcMasterId } from '../../utils'
import { ControllerDto, MyPvControllerDto, StorageControllerDto } from '../api/sub-devices'
import {
  getDefaultStorageSystem,
  InstallationFormModel,
  IoInterfaces,
  PriorityChargingDevice,
  TrackersBehaviour,
  WeatherOptimizationOption,
} from '../forms'
import {
  useAccountLinks,
  useEmcConfig,
  useEmcIdlePeriods,
  useInstallation,
  useInstallationConfig,
  useInstallationDevices,
  useInstallationSubDeviceQueries,
  useInstallationSubDevices,
  useModbusTcpServer,
  useStorageBatteryOverviewQueries,
  useStorageConfigQueries,
} from '../hooks/installation-fetch-hooks'
import { getUseStorageSystemEnergySavingMode } from '../utils'
import { getTimeAsDate } from '../utils/date-time'

type UseFetchInstallation = {
  installation: InstallationFormModel | null
  canEdit: boolean
  isLoading: boolean
}

//#region useFetchInstallation
/**
 * Fetches all the data about an installation, then parses and agggregates it for
 * the installation form.
 */
export const useFetchInstallation = (installationId: number | undefined, enabled = true): UseFetchInstallation => {
  const { t } = useCustomTranslation()

  const { data: installationBase, isLoading: installationLoading } = useInstallation(installationId, enabled)
  const { data: installationConfig, isLoading: installationConfigLoading } = useInstallationConfig(installationId, enabled)
  const { data: emcIdlePeriods, isLoading: emcIdlePeriodsLoading } = useEmcIdlePeriods(installationId, enabled)

  const { data: devices, isLoading: devicesLoading } = useInstallationDevices(installationId, enabled)

  /**
   * EmcId from router state.
   * The id is already fetched in installations overview, so it is passed "pre-fetched" here if it is available.
   */
  const emcMasterId = useLocationEmcMasterId()
  const emcId: string | null = emcMasterId ?? devices?.emc?.id?.toString() ?? null

  const { data: emcConfig, isLoading: emcConfigLoading } = useEmcConfig(installationId, emcId)

  const storageIds: string[] = devices?.storages?.map(storage => storage?.id?.toString()).filter((id): id is string => Boolean(id)) || []
  const storageConfigQueries = useStorageConfigQueries(installationId, storageIds, enabled)
  // * need to extract data from queries and put them in a useMemo, otherwise the installationState memoization breaks
  const storageConfigsData = useMemo(
    () => storageConfigQueries.map(q => q.data),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storageConfigQueries.map(q => `${q.status}:${q.dataUpdatedAt}`).join('|')]
  )

  const storageIdsAndEmcId = emcId && storageIds.length > 0 ? [emcId, ...storageIds] : [emcId]
  const subDevicesQueries = useInstallationSubDeviceQueries(installationId, storageIdsAndEmcId, enabled)
  const { data: subDevices, isLoading: subDevicesLoading } = useInstallationSubDevices(installationId, emcId, enabled)
  // * need to extract data from queries and put them in a useMemo, otherwise the installationState memoization breaks
  const subDevicesData = useMemo(
    () => subDevicesQueries.map(q => q.data),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subDevicesQueries.map(q => `${q.status}:${q.dataUpdatedAt}`).join('|')]
  )

  const pairedStorageHardwareDeviceIds: string[] =
    devices?.storages
      ?.filter(storage => storage.pairingState === 'paired')
      .map(storage => storage.deviceId?.toString())
      .filter((deviceId): deviceId is string => Boolean(deviceId)) || []
  const storageBatteryOverviewQueries = useStorageBatteryOverviewQueries(installationId, pairedStorageHardwareDeviceIds, enabled)
  // * need to extract data from queries and put them in a useMemo, otherwise the installationState memoization breaks
  const storageBatteryOverviewsData = useMemo(
    () => storageBatteryOverviewQueries.map(q => q.data),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storageBatteryOverviewQueries.map(q => `${q.status}:${q.dataUpdatedAt}`).join('|')]
  )
  /**
   * Installation content that are not part of the form but are nessesary for the complete loading of the page.
   */
  const { isLoading: badgesLoading } = useGetBadges(installationId, enabled)
  const { isLoading: eebusInfoLoading } = useFetchEebusInfoFromEmc(installationId, emcId, enabled)
  const { isLoading: smartMeterGatewayInfoLoading } = useFetchSmartMeterGateway(installationId, emcId, enabled)
  const { isLoading: accountsLoading } = useAccountLinks(installationId, enabled)
  const { getInvitations } = useInstallationInvitation()
  const { isLoading: invitationsLoading } = getInvitations(installationId, enabled && allowAccess(installationBase?.endUserPermissionType))
  const { isLoading: modbusTcpServerLoading } = useModbusTcpServer(installationId, enabled)

  const extraContentLoading =
    badgesLoading || eebusInfoLoading || smartMeterGatewayInfoLoading || accountsLoading || invitationsLoading || modbusTcpServerLoading

  const isLoading =
    installationLoading ||
    installationConfigLoading ||
    emcIdlePeriodsLoading ||
    devicesLoading ||
    subDevicesLoading ||
    emcConfigLoading ||
    storageConfigQueries.some(storageConfigQuery => storageConfigQuery.isLoading) ||
    storageBatteryOverviewQueries.some(storageBatteryOverviewQuery => storageBatteryOverviewQuery.isLoading) ||
    subDevicesQueries.some(query => query.isLoading) ||
    extraContentLoading

  const chargeIdlePeriods = useMemo(() => (emcIdlePeriods ?? []).filter(period => period.mode === 'charge'), [emcIdlePeriods])
  const dischargeIdlePeriods = useMemo(() => (emcIdlePeriods ?? []).filter(period => period.mode === 'discharge'), [emcIdlePeriods])
  const emcDevice = useMemo(() => (devices?.emc ? parseEmcDeviceLink(devices.emc) : null), [devices?.emc])
  const storageDevices = useMemo(
    () => devices?.storages?.map(storage => ({ ...parseStorageDeviceLink(storage, t), deviceType: getStorageType(storage) })) || [],
    [devices?.storages, t]
  )
  const defaultStorageSystem = useMemo(() => getDefaultStorageSystem(emcDevice, storageDevices), [emcDevice, storageDevices])
  const chargingStations = useMemo(() => parseChargingStations(subDevices?.controlled ?? []), [subDevices?.controlled])

  const installationState: UseFetchInstallation = useMemo(() => {
    const installation: InstallationFormModel = {
      id: installationBase?.id ?? null,
      name: installationBase?.projectName ?? '',
      emcDevice,
      storageDevices,
      gpsLatitude: installationBase?.gpsLatitude?.toString() ?? '',
      gpsLongitude: installationBase?.gpsLongitude?.toString() ?? '',

      generalSettings: {
        location: {
          street: installationBase?.streetName ?? '',
          houseNumber: installationBase?.streetNumber ?? '',
          postalCode: installationBase?.zip ?? '',
          location: installationBase?.city ?? '',
          country: installationBase?.countryCode ?? countryCodes.germany,
          timeZone: installationBase?.timezone ?? 'Europe/Berlin',
        },

        gridSettings: {
          numberOfPhases: installationConfig?.phaseCount === 1 ? 'single-phase' : 'three-phases',
          mainProtection: installationConfig?.mainFuse ?? 63,
          thermalDerating: installationConfig?.mainFuseDerated ?? 50,
          gridMeter: {
            hasElectricalCurrentTransformer: emcConfig?.useCT ?? false,
            electricalCurrentTransformerRatio: emcConfig?.ratioCT ?? '400:5',
          },
        },
      },

      storageSystem: {
        generalSettings: {
          wiringTopologyDeviceId: installationConfig?.rootPowerMeter ?? defaultStorageSystem.generalSettings.wiringTopologyDeviceId,

          systemRegulation: {
            peakPower: installationConfig?.peakPowerPv ?? defaultStorageSystem.generalSettings.systemRegulation.peakPower,
            hasFeedInLimit: installationConfig?.eegDerateAtConnectedPower ?? defaultStorageSystem.generalSettings.systemRegulation.hasFeedInLimit,
            feedInRatio: installationConfig?.eegValue ?? defaultStorageSystem.generalSettings.systemRegulation.feedInRatio,
            feedInMaxPower: installationConfig?.eegConnectedPower ?? defaultStorageSystem.generalSettings.systemRegulation.feedInMaxPower,
          },

          chargingConfiguration: {
            priorityChargingDevice: getPriorityChargingDevice('charging station', installationConfig?.emsBatteryBeforeCarMode),
            useBatteryDischargeOnlyWhenPvIsExceeded:
              installationConfig?.emsBatteryToCarMode ??
              defaultStorageSystem.generalSettings.chargingConfiguration.useBatteryDischargeOnlyWhenPvIsExceeded,
            useBatteryDischargeOnlyInOtherMode:
              installationConfig?.emsWallboxEnforcePowerAssignment ??
              defaultStorageSystem.generalSettings.chargingConfiguration.useBatteryDischargeOnlyInOtherMode,
          },

          advancedSettings: {
            weatherForecastOptimization: {
              useWeatherForecastBasedCharging:
                installationConfig?.emsWeatherRegulatedChargeEnabled ??
                defaultStorageSystem.generalSettings.advancedSettings.weatherForecastOptimization.useWeatherForecastBasedCharging,
              weatherOptimizationOption: getWeatherOptimizationOption(
                defaultStorageSystem.generalSettings.advancedSettings.weatherForecastOptimization.weatherOptimizationOption,
                installationConfig?.sgrCooldown
              ),
            },
            emergencyPower: {
              useEmergencyPower:
                installationConfig?.emergencyPowerCheckPartialGridFailure ??
                defaultStorageSystem.generalSettings.advancedSettings.emergencyPower.useEmergencyPower,
              emergencyPowerDelay:
                installationConfig?.emergencyPowerDelayInSeconds ??
                defaultStorageSystem.generalSettings.advancedSettings.emergencyPower.emergencyPowerDelay,
            },

            chargeBlockingWeeklySchedule: chargeIdlePeriods.map(period => ({
              fromTime: getTimeAsDate(period.from ?? ''),
              toTime: getTimeAsDate(period.to ?? ''),
              weekDays: {
                monday: period.days?.mo ?? false,
                tuesday: period.days?.tu ?? false,
                wednesday: period.days?.we ?? false,
                thursday: period.days?.th ?? false,
                friday: period.days?.fr ?? false,
                saturday: period.days?.sa ?? false,
                sunday: period.days?.su ?? false,
              },
            })),

            dischargeBlockingWeeklySchedule: dischargeIdlePeriods.map(period => ({
              fromTime: getTimeAsDate(period.from ?? ''),
              toTime: getTimeAsDate(period.to ?? ''),
              weekDays: {
                monday: period.days?.mo ?? false,
                tuesday: period.days?.tu ?? false,
                wednesday: period.days?.we ?? false,
                thursday: period.days?.th ?? false,
                friday: period.days?.fr ?? false,
                saturday: period.days?.sa ?? false,
                sunday: period.days?.su ?? false,
              },
            })),
          },
        },

        storageSpecificSettings: storageDevices
          .map(storageDevice => {
            const storageConfig = storageConfigsData.find(
              storageConfigQuery => storageConfigQuery?.deviceLinkId?.toString() === storageDevice?.deviceId
            )
            const storageBatteryOverview = storageBatteryOverviewsData.find(
              storageOverviewQuery => storageOverviewQuery?.storageHardwareDeviceId === storageDevice?.storageHardwareDeviceId
            )
            const defaultStorageSpecificSettings = defaultStorageSystem.storageSpecificSettings.find(
              settings => settings.deviceId === storageDevice.deviceId
            )

            if (!defaultStorageSpecificSettings) return null
            return {
              deviceId: storageDevice?.deviceId || null,

              chargingConfiguration: {
                trackersBehaviour: getTrackersBehavior(
                  defaultStorageSpecificSettings.chargingConfiguration.trackersBehaviour,
                  storageConfig?.pvInverterStringCount
                ),
              },

              advancedSettings: {
                batteryNumber: storageBatteryOverview?.batDcbCount ?? null,

                batteryOperationRange: {
                  isStorageSingleBattery:
                    (storageDevice?.deviceType === 'XCM - R2' && storageBatteryOverview?.batDcbCount === 1) ||
                    storageDevice?.deviceType === 'XEM - R1',
                  isAuto: storageConfig?.emsOperatingRangeAuto ?? defaultStorageSpecificSettings.advancedSettings.batteryOperationRange.isAuto,
                  maximumChargingPower:
                    storageConfig?.emsMaxChargePower ?? defaultStorageSpecificSettings.advancedSettings.batteryOperationRange.maximumChargingPower,
                  maximumDischargingPower:
                    storageConfig?.emsMaxDischargePower ??
                    defaultStorageSpecificSettings.advancedSettings.batteryOperationRange.maximumDischargingPower,
                  minimumConsumptionPowerToStartDischarge:
                    storageConfig?.emsDischargeStartPower ??
                    defaultStorageSpecificSettings.advancedSettings.batteryOperationRange.minimumConsumptionPowerToStartDischarge,
                },

                emergencyPower: {
                  emergencyPowerReserve:
                    storageConfig?.emergencyPowerCapacityReserve ??
                    defaultStorageSpecificSettings.advancedSettings.emergencyPower.emergencyPowerReserve,
                },

                useStorageSystemEnergySavingMode: getUseStorageSystemEnergySavingMode(storageConfig?.emsPowersaveEnabled),
              },
            }
          })
          .filter((settings): settings is NonNullable<typeof settings> => settings !== null),
      },
      chargingStations,
      chargingStationsExpandedState: getCharginStationExpandedState(chargingStations),
      heatpump: (() => {
        const heatpumpDto = subDevices?.controllers?.find(io => io.type === 'HpWithISG')
        return heatpumpDto ? fromHeatpumpDtoToModel(heatpumpDto) : null
      })(),
      meters:
        subDevices?.monitoring?.meters
          .sort((a, b) => (a.mediaParameters?.address || 0) - (b.mediaParameters?.address || 0))
          .map(meter => fromMeterDtoToModel(meter)) ?? [],
      ioInterfaces: separateIoDevicesArray(subDevicesData.map(subDevicesQuery => subDevicesQuery?.controllers ?? [])),
    }
    if (isLoading) return { installation: null, canEdit: false, isLoading: true }

    return {
      installation: installation,
      canEdit: installationBase?.permissionType === 'rw',
      isLoading: false,
    }
  }, [
    isLoading,
    installationBase,
    installationConfig,
    subDevices,
    emcConfig,
    storageConfigsData,
    storageBatteryOverviewsData,
    defaultStorageSystem,
    chargeIdlePeriods,
    dischargeIdlePeriods,
    chargingStations,
    emcDevice,
    storageDevices,
    subDevicesData,
  ])

  return installationState
}
//#endregion

//#region Helper
const getTrackersBehavior = (defaultValue: TrackersBehaviour, pvInverterStringCount?: number | null): TrackersBehaviour => {
  switch (pvInverterStringCount) {
    case 1:
      return 'single tracker'
    case 2:
      return 'duo tracker'
    default:
      return defaultValue
  }
}

const getPriorityChargingDevice = (defaultValue: PriorityChargingDevice, emsBatteryBeforeCarMode?: boolean | null): PriorityChargingDevice => {
  if (emsBatteryBeforeCarMode === undefined || emsBatteryBeforeCarMode === null) return defaultValue
  return emsBatteryBeforeCarMode ? 'battery' : 'charging station'
}

const getWeatherOptimizationOption = (defaultValue: WeatherOptimizationOption, sgrCooldown?: boolean | null): WeatherOptimizationOption => {
  if (sgrCooldown == null || sgrCooldown === undefined) return defaultValue
  return sgrCooldown ? 'Heat pump via SG-Ready' : 'Storage batteries'
}

const separateIoDevicesArray = (controllersDto: Nullable<ControllerDto>[][] | undefined): IoInterfaces => {
  if (!controllersDto)
    return {
      htc320h: null,
      mypv: null,
      storage: null,
    }

  const htc320hDto = controllersDto.flat().find(io => io.type === 'IOController' && io.media === 'ModbusRTU')
  const storageDto = controllersDto.flat().find(io => io.type === 'IOController' && io.media === 'Storage') as StorageControllerDto
  const mypvDto = controllersDto.flat().find(io => io.type === 'MyPvHeater') as Nullable<MyPvControllerDto>

  return {
    htc320h: htc320hDto ? parseHtc320hIo(htc320hDto) : null,
    storage: storageDto ? parseStorageIo(storageDto) : null,
    mypv: mypvDto ? parseMyPvIo(mypvDto) : null,
  }
}
//#endregion
